import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material"
import { Upload, Info, AlertTriangle } from "lucide-mui"
import React, { useRef, useState } from "react"

import i18n from "@/i18n"

import { isValidSvg } from "./svg_utils"

interface VenuePlanUploaderProps {
  hasExistingPlan: boolean
  onFileSelected: (svgContent: string) => void
  onError: (error: string) => void
  iconButton?: boolean
  className?: string
}

const VenuePlanUploader: React.FC<VenuePlanUploaderProps> = ({
  hasExistingPlan,
  onFileSelected,
  onError,
  iconButton = false,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      onError(i18n.t("app.settings.venues.venue_layout.plan.no_file_selected"))
      return
    }

    if (selectedFile.type !== "image/svg+xml" && !selectedFile.name.endsWith(".svg")) {
      onError(i18n.t("app.settings.venues.venue_layout.plan.invalid_file_type"))
      return
    }

    try {
      const content = await selectedFile.text()

      if (!isValidSvg(content)) {
        onError(i18n.t("app.settings.venues.venue_layout.plan.invalid_svg"))
        return
      }

      onFileSelected(content)
      setDialogOpen(false)
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    } catch (_err) {
      onError(i18n.t("app.settings.venues.venue_layout.plan.upload_error"))
    }
  }

  const handleCancel = () => {
    setDialogOpen(false)
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const buttonText = hasExistingPlan
    ? i18n.t("app.settings.venues.venue_layout.plan.replace_plan")
    : i18n.t("app.settings.venues.venue_layout.plan.upload_new_plan")

  return (
    <>
      {iconButton ? (
        <IconButton
          onClick={() => setDialogOpen(true)}
          size="small"
          className={className}
          data-testid="settings-venue-plan-uploader-btn-icon"
        >
          <Upload className="w-5 h-5" />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          startIcon={<Upload className="w-4 h-4" />}
          onClick={() => setDialogOpen(true)}
          data-testid="settings-venue-plan-uploader-btn"
        >
          {buttonText}
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        data-testid="settings-venue-plan-uploader-dialog"
      >
        <DialogTitle className="mt-0">
          {hasExistingPlan
            ? i18n.t("app.settings.venues.venue_layout.plan.replace_plan")
            : i18n.t("app.settings.venues.venue_layout.plan.upload_new_plan")}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            {hasExistingPlan ? (
              <div className="bg-amber-50 border border-solid border-amber-300 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0" />
                <div className="text-sm text-amber-900 font-semibold">
                  {i18n.t("app.settings.venues.venue_layout.plan.replace_warning_message")}
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                onChange={handleFileChange}
                className="hidden"
                data-testid="settings-venue-plan-uploader-file-input"
              />
              <Button
                variant="outlined"
                startIcon={<Upload className="w-4 h-4" />}
                onClick={() => fileInputRef.current?.click()}
                className="min-w-40 max-w-full"
                data-testid="settings-venue-plan-uploader-btn-select-file"
              >
                {i18n.t("app.settings.venues.venue_layout.plan.select_file")}
              </Button>
              {selectedFile ? <span className="text-sm text-gray-600">{selectedFile.name}</span> : null}
            </div>

            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-gray-500" />
              <div className="text-sm text-gray-500">{i18n.t("app.settings.venues.venue_layout.plan.upload_info")}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit" data-testid="settings-venue-plan-uploader-btn-cancel">
            {i18n.t("app.settings.venues.venue_layout.cancel")}
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="primary"
            disabled={!selectedFile}
            data-testid="settings-venue-plan-uploader-btn-upload"
          >
            {i18n.t("app.settings.venues.venue_layout.plan.upload_plan")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default VenuePlanUploader
